/**
 * Chapter system — now GENERATED from the full level pool instead of a fixed
 * 6×6 hand-map, so it scales to 100+ levels automatically.
 *
 * Ordering: every level is sorted by difficulty tier, and within a tier the
 * curated hand-authored levels (which carry intros/hints and teach a mechanic)
 * lead, followed by the procedurally generated ones. The sorted list is then
 * chunked into fixed-size chapters. Adding levels (hand or generated) reflows
 * the chapters with no manual bookkeeping.
 */

import { LEVELS, HAND_LEVELS } from './index';
import { isProceduralId, proceduralIndex, proceduralCodename, isDailyId, dailyKeyFromId, dailyCodename } from './procgen';
import { t } from '../i18n';
import { CODA_LEVELS } from './coda';
import type { LevelDef } from '../sim/types';
import type { StringKey } from '../i18n/en';

export type ChapterTier =
  | 'tutorial' | 'beginner' | 'easy' | 'medium'
  | 'hard' | 'expert' | 'brutal' | 'nightmare';

export interface ChapterDef {
  id: string;
  num: number;
  name: string;
  codeName: string;
  tagline: string;
  tier: ChapterTier;
  accent: number;
  accentHex: string;
  /** Optional locale keys for the title and tagline (the themed pool stays English by design). */
  nameKey?: StringKey;
  taglineKey?: StringKey;
  /** Level ids in this chapter, in order. (No null placeholders any more.) */
  slots: (string | null)[];
}

export const CHAPTER_SLOTS = 8;
const CHAPTER_SIZE = 8;

const TIER_RANK: Record<ChapterTier, number> = {
  tutorial: 0, beginner: 1, easy: 2, medium: 3,
  hard: 4, expert: 5, brutal: 6, nightmare: 7,
};

const TIER_ACCENT: Record<ChapterTier, { n: number; hex: string }> = {
  tutorial:  { n: 0x88e8ff, hex: '#88e8ff' },
  beginner:  { n: 0x88e8ff, hex: '#88e8ff' },
  easy:      { n: 0x7ad0ff, hex: '#7ad0ff' },
  medium:    { n: 0xffd070, hex: '#ffd070' },
  hard:      { n: 0xff9d4a, hex: '#ff9d4a' },
  expert:    { n: 0x88ffb0, hex: '#88ffb0' },
  brutal:    { n: 0xff5577, hex: '#ff5577' },
  nightmare: { n: 0xdd99ff, hex: '#dd99ff' },
};

// Themed chapter name/codename/tagline pool. Cycled if there are more chapters
// than entries (suffix keeps them distinct).
const CHAPTER_THEMES: { name: string; code: string; tagline: string }[] = [
  { name: 'PRIMER',      code: 'FIRST.RUN',     tagline: 'Boot the cascade. Learn the syntax.' },
  { name: 'QUARANTINE',  code: 'REDLINE',       tagline: 'Red nodes will end the run.' },
  { name: 'CONVERGENCE', code: 'SIGNAL.SYNC',   tagline: 'Pulses must land in the same window.' },
  { name: 'TRIPWIRE',    code: 'DEAD.LINE',     tagline: 'One forbidden line ends everything.' },
  { name: 'SHIELDS',     code: 'AEGIS.BREAK',   tagline: 'Drop the guard. Land the shot.' },
  { name: 'CITADEL',     code: 'CORE.WIPE',     tagline: 'The whole machine, one tap.' },
  { name: 'BLACKSITE',   code: 'NO.WITNESS',    tagline: 'Every path is watched. Pick the blind one.' },
  { name: 'OVERCLOCK',   code: 'BURNOUT',       tagline: 'Timing is everything now.' },
  { name: 'LABYRINTH',   code: 'NO.MAP',        tagline: 'Trace it all before you commit.' },
  { name: 'REACTOR',     code: 'MELTDOWN',      tagline: 'Flood the core. Mind the shields.' },
  { name: 'ECLIPSE',     code: 'LIGHTS.OUT',    tagline: 'The machine fights back.' },
  { name: 'ABYSS',       code: 'TERMINAL',      tagline: 'Past here, no mistakes survive.' },
  { name: 'ZERO.DAY',    code: 'ENDGAME',       tagline: 'Everything you learned, at once.' },
  { name: 'PROTOCOL',    code: 'UNDEFINED',     tagline: 'Reserved for the next protocol.' },
];

/** Difficulty-sorted master order: tier asc, curated before generated within a tier. */
function masterOrder(): LevelDef[] {
  const handIds = new Set(HAND_LEVELS.map(l => l.id));
  const numId = (id: string) => parseInt(id.replace(/^\D+/, ''), 10) || 0;
  const coda = new Set(CODA_LEVELS.map(l => l.id));
  return LEVELS.filter(l => !coda.has(l.id)).sort((a, b) => {
    const tr = TIER_RANK[a.tier] - TIER_RANK[b.tier];
    if (tr !== 0) return tr;
    const ca = handIds.has(a.id) ? 0 : 1;
    const cb = handIds.has(b.id) ? 0 : 1;
    if (ca !== cb) return ca - cb;
    return numId(a.id) - numId(b.id);
  });
}

function buildChapters(): ChapterDef[] {
  const order = masterOrder();
  const chapters: ChapterDef[] = [];
  for (let i = 0; i < order.length; i += CHAPTER_SIZE) {
    const chunk = order.slice(i, i + CHAPTER_SIZE);
    const num = chapters.length + 1;
    // Chapter tier = hardest tier in the chunk (drives accent + mood).
    const tier = chunk.reduce<ChapterTier>(
      (acc, l) => (TIER_RANK[l.tier] > TIER_RANK[acc] ? l.tier : acc),
      chunk[0].tier,
    );
    const theme = CHAPTER_THEMES[(num - 1) % CHAPTER_THEMES.length];
    const cycle = Math.floor((num - 1) / CHAPTER_THEMES.length);
    const accent = TIER_ACCENT[tier];
    chapters.push({
      id: 'ch' + num,
      num,
      name: cycle > 0 ? `${theme.name}.${cycle + 1}` : theme.name,
      codeName: theme.code,
      tagline: theme.tagline,
      tier,
      accent: accent.n,
      accentHex: accent.hex,
      slots: chunk.map(l => l.id),
    });
  }
  // CODA: one closing chapter of 20, always last, played in authored order
  // (it is difficulty-ramped by hand, so it does not go through the tier sort).
  if (CODA_LEVELS.length > 0) {
    const tier = CODA_LEVELS.reduce<ChapterTier>(
      (acc, l) => (TIER_RANK[l.tier] > TIER_RANK[acc] ? l.tier : acc),
      CODA_LEVELS[0].tier,
    );
    const accent = TIER_ACCENT[tier];
    chapters.push({
      id: 'ch' + (chapters.length + 1),
      num: chapters.length + 1,
      name: 'CODA',
      codeName: 'RE.ENTRY',
      tagline: 'Nothing new. Everything combined.',
      nameKey: 'chapter_coda_name',
      taglineKey: 'chapter_coda_tagline',
      tier,
      accent: accent.n,
      accentHex: accent.hex,
      slots: CODA_LEVELS.map(l => l.id),
    });
  }
  return chapters;
}

export const CHAPTERS: ChapterDef[] = buildChapters();

export function chapterName(ch: ChapterDef): string {
  return ch.nameKey ? t(ch.nameKey) : ch.name;
}
export function chapterTagline(ch: ChapterDef): string {
  return ch.taglineKey ? t(ch.taglineKey) : ch.tagline;
}

export function getChapter(id: string): ChapterDef | undefined {
  return CHAPTERS.find(c => c.id === id);
}

export function getChapterForLevel(levelId: string): ChapterDef | undefined {
  return CHAPTERS.find(c => c.slots.includes(levelId));
}

/** All level ids that are present (non-null) in any chapter, in order. */
export function orderedLevelIds(): string[] {
  const out: string[] = [];
  for (const c of CHAPTERS) for (const s of c.slots) if (s) out.push(s);
  return out;
}

/** Levels that exist in code AND are slotted into some chapter. */
export function orderedLevels(): LevelDef[] {
  const byId = new Map(LEVELS.map(l => [l.id, l]));
  return orderedLevelIds().map(id => byId.get(id)).filter((l): l is LevelDef => !!l);
}

// ── codenames ────────────────────────────────────────────────────────────────
// Curated levels keep their hand-picked opcodes; generated levels derive a
// stable codename from their display name + a hex index suffix.
const LEVEL_CODENAMES: Record<string, string> = {
  l1: 'BOOT.01', l2: 'ARROW.02', l3: 'TRIAGE.03', l13: 'CHAIN.04', l14: 'FORK.05', l15: 'MERGE.06',
  l4: 'MASK.07', l16: 'DETOUR.08', l17: 'MIRROR.09', l8: 'DEFRAG.0A', l19: 'BAIT.0B',
  l5: 'SYNC.0C', l20: 'TWIN.0D', l6: 'CAP.0E', l21: 'BUFFER.0F', l9: 'TRIPWIRE.10',
  l23: 'PIERCE.11', l10: 'AEGIS.12', l25: 'FANOUT.13', l11: 'CROSSROAD.14', l7: 'OVERLOAD.15',
  l12: 'CITADEL.16', l26: 'REACTOR.17', l27: 'OMNIBUS.18',
};

const GEN_CODENAMES: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  const byId = new Map(LEVELS.map(l => [l.id, l]));
  // CODA levels are named by play position (the hex index continues the campaign count).
  const codaFirst = LEVELS.length - CODA_LEVELS.length;
  CODA_LEVELS.forEach((l, i) => {
    map[l.id] = `${l.name.toUpperCase().replace(/\s+/g, '')}.${(codaFirst + i + 1).toString(16).toUpperCase().padStart(2, '0')}`;
  });
  for (const l of LEVELS) {
    if (LEVEL_CODENAMES[l.id] || map[l.id]) continue;
    const lvl = byId.get(l.id)!;
    const n = parseInt(l.id.replace(/^\D+/, ''), 10) || 0;
    map[l.id] = `${(lvl.name || l.id).toUpperCase()}.${n.toString(16).toUpperCase().padStart(2, '0')}`;
  }
  return map;
})();

export function codenameOf(levelId: string): string {
  if (isProceduralId(levelId)) return proceduralCodename(proceduralIndex(levelId));
  if (isDailyId(levelId)) return dailyCodename(dailyKeyFromId(levelId));
  return LEVEL_CODENAMES[levelId] ?? GEN_CODENAMES[levelId] ?? levelId.toUpperCase();
}
